import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import {
  ElencoDocumentiResponse,
  NumeroAllegatiDocumentiRes,
  NumeroAllegatiDocumentiResponse,
  PollingStatoDocumentiRequest,
  PollingStatoDocumentiResponse,
  RichiestaInvioDocumentoRes,
  RichiestaInvioDocumentiResponse,
} from '../models/interfaces/elenco-documenti.interface';
import { type DettaglioDocumentoI } from '../models/interfaces/documenti-adempimenti/dettaglio-documento.interface';
import { HttpClientConte } from './http-client-conte';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class DocumentiAdempimentiGestioneDocumentiService {
  private readonly endpoint = `${environment.CONTEXT_DOCUMENTAZIONE}/documenti-adempimenti`;

  constructor(private readonly httpClientConte: HttpClientConte) {}

  /**
   * Servizio per la gestione della logica e chiamate nel tab DOCUMENTI ADEMPIMENTI.
   */
  getElencoDocumenti(page: number = 0, size: number = 10, params?:string): Observable<ElencoDocumentiResponse | null> {
    let url = params ? `${this.endpoint}?${params}&page=${page}&size=${size}` : `${this.endpoint}?page=${page}&size=${size}`;

    return this.httpClientConte.httpGet<ElencoDocumentiResponse>(url).pipe(
      catchError((error) => {
        console.error('[DocumentiAdempimentiGestioneDocumentiService] Error fetching elenco documenti:', error);
        return of(null);
      }),
    );
  }

  /**
   * Sostituisce il file di un documento già presente.
   * PATCH `/documentazione/documenti-adempimenti/{sequDocumento}/file`.
   * Multipart: `request` (JSON, solo metadati file) + `file`.
   * La risposta è l'id del documento.
   */
  ricaricaFileDocumento(sequDocumento: number, payload: FormData): Observable<number> {
    return this.httpClientConte.httpPatch<number>(`${this.endpoint}/${sequDocumento}/file`, payload);
  }

  /** GET dettaglio documento (senza spinner: usato anche per refresh post-polling). */
  getDettaglioDocumento(sequDocumento: number): Observable<DettaglioDocumentoI | null> {
    const url = `${this.endpoint}/${sequDocumento}`;

    return this.httpClientConte.httpGet<DettaglioDocumentoI>(url).pipe(
      catchError((error) => {
        console.error('[DocumentiAdempimentiGestioneDocumentiService] Error fetching dettaglio documento:', error);
        return of(null);
      }),
    );
  }

  getStatoDocumentiPolling(payload: PollingStatoDocumentiRequest): Observable<PollingStatoDocumentiResponse | null> {
    const url = `${this.endpoint}/stato`;

    return this.httpClientConte.httpPost<PollingStatoDocumentiResponse>(url, payload).pipe(
      catchError((error) => {
        console.error('[DocumentiAdempimentiGestioneDocumentiService] Error polling stato documenti:', error);
        return of(null);
      }),
    );
  }

  /**
   * @desc chiamata POST che restituisce il numero di allegati per ogni documento
   * @param listaSequDocumento - array di identificativi dei documenti
   * @returns Observable con la lista di { sequDocumento, nAllegati } per ogni documento
   */
  postNumeroAllegati(listaSequDocumento: number[]): Observable<NumeroAllegatiDocumentiRes[]> {
    const url = `${this.endpoint}/n-allegati`;

    return this.httpClientConte.httpPost<NumeroAllegatiDocumentiResponse>(url, { listaSequDocumento }).pipe(
      map((response) => response?.data ?? []),
      catchError((error) => {
        console.error('[DocumentiAdempimentiGestioneDocumentiService] Error fetching numero allegati:', error);
        return of([] as NumeroAllegatiDocumentiRes[]);
      }),
    );
  }

  /**
   * @desc POST che indica se l'azione "Richiesta nuovo invio" è abilitata per ogni documento
   * e il relativo `tooltipText` da mostrare quando il bottone è disabilitato.
   */
  postRichiestaInvioAbilitata(listaSequDocumento: number[]): Observable<RichiestaInvioDocumentoRes[]> {
    const url = `${this.endpoint}/richiesta-invio`;

    return this.httpClientConte.httpPost<RichiestaInvioDocumentiResponse | RichiestaInvioDocumentoRes[]>(
      url,
      { listaSequDocumento },
    ).pipe(
      map((response) => {
        if (Array.isArray(response)) {
          return response;
        }
        const data = response?.data;
        if (Array.isArray(data)) {
          return data;
        }
        return [];
      }),
      catchError((error) => {
        console.error(
          '[DocumentiAdempimentiGestioneDocumentiService] Error fetching richiesta invio abilitata:',
          error,
        );
        return of([] as RichiestaInvioDocumentoRes[]);
      }),
    );
  }

}
